import React from 'react';
import Icon from './ui/Icon';
import Reveal, { RevealGroup } from './ui/Reveal';
import SectionLabel from './ui/SectionLabel';
import photo from '../lib/images';
import { story } from '../data/content';

export default function OurStory() {
  return (
    <section id="story" className="bg-cream-200 py-24 sm:py-32">
      <div className="shell grid items-start gap-14 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16">
        <div>
          <Reveal variant="fade">
            <SectionLabel index="01">Our Story</SectionLabel>
          </Reveal>

          <Reveal
            as="h2"
            delay={80}
            className="mt-7 font-display text-[2rem] font-semibold uppercase leading-[1.08] tracking-[0.01em] text-ink sm:text-4xl lg:text-[3rem]"
          >
            Our Story
          </Reveal>
          <Reveal variant="rule" delay={220} className="rule mt-6" />

          <Reveal as="p" delay={280} className="mt-8 text-[0.98rem] leading-relaxed text-ink/75">
            {story.intro}
          </Reveal>

          <RevealGroup as="ul" variant="left" stagger={80} delay={120} className="mt-7 space-y-3">
            {story.burdens.map((item) => (
              <li key={item} className="flex gap-3.5 text-[0.98rem] leading-relaxed text-ink/80">
                <span className="mt-[3px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-gold-600 text-white">
                  <Icon name="check" className="h-3 w-3" strokeWidth={2.6} />
                </span>
                {item}
              </li>
            ))}
          </RevealGroup>

          <Reveal as="p" className="mt-7 text-[0.98rem] leading-relaxed text-ink/75">
            {story.outro}
          </Reveal>
        </div>

        <div>
          <RevealGroup as="ul" variant="mask" stagger={90} className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {story.images.map((image) => (
              <li key={image.name} className="group overflow-hidden bg-ink/5">
                <img
                  {...photo(image.name)}
                  sizes="(min-width: 1024px) 20vw, (min-width: 640px) 30vw, 45vw"
                  alt={image.alt}
                  width="900"
                  height="600"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[3/2] w-full object-cover brightness-[0.97] transition-all duration-[900ms] ease-out-expo group-hover:scale-[1.06] group-hover:brightness-105"
                />
              </li>
            ))}
          </RevealGroup>

          {/* The dark caption panel that sits under the collage, with a torn top
              edge echoing a brush stroke. */}
          <Reveal
            delay={280}
            className="on-dark grain relative -mt-3 bg-ink-900 px-7 pb-8 pt-11 sm:px-10"
            style={{ clipPath: 'polygon(0 16px, 22% 4px, 55% 13px, 78% 2px, 100% 11px, 100% 100%, 0 100%)' }}
          >
            <div className="relative flex items-center gap-5">
              <Icon name="hand-heart" className="h-10 w-10 shrink-0 text-gold" />
              <p className="font-display text-lg uppercase tracking-[0.1em] text-cream/65 sm:text-xl">
                {story.banner[0]}
                <span className="mt-1.5 block font-semibold tracking-[0.04em] text-cream">{story.banner[1]}</span>
              </p>
            </div>
          </Reveal>

          {/* "A place with land. Gardens. Animals. Work. …" — the brief lists
              these one per line; as chips beneath the collage they read as one
              idea, and they balance the column against the longer text opposite. */}
          <RevealGroup as="ul" variant="scale" stagger={45} className="mt-9 flex flex-wrap gap-2">
            {story.place.map((item) => (
              <li
                key={item}
                className="border border-ink/15 px-3.5 py-1.5 font-display text-[0.7rem] uppercase tracking-[0.14em] text-ink/70 transition-colors duration-300 hover:border-gold hover:text-gold-700"
              >
                {item}
              </li>
            ))}
          </RevealGroup>

          <Reveal as="p" className="mt-7 text-[0.98rem] leading-relaxed text-ink/75">
            {story.notGiven.lead}
            <span className="font-medium text-ink">{story.notGiven.emphasis}</span>
            {story.notGiven.trail}
          </Reveal>
          <Reveal as="p" delay={100} className="mt-5 font-display text-lg uppercase tracking-[0.06em] text-gold-700">
            {story.developed}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
